"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { MapPin } from "lucide-react";
import HoverArrow from "@/components/shared/HoverArrow";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { uniDetailPath, uniImagePath, type University } from "@/lib/universities";

export default function RelatedUniversities({ universities }: { universities: University[] }) {
  if (!universities.length) return null;

  return (
    <section className="cv-auto bg-paper border-t border-line py-20 lg:py-24" aria-labelledby="related-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger(0.09)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="flex flex-col gap-4 mb-10"
        >
          <motion.p
            variants={fadeUp}
            className="flex items-center gap-3 text-xs font-bold uppercase tracking-widest text-ink-faint"
          >
            <span className="w-1.5 h-1.5 bg-accent shrink-0" aria-hidden="true" />
            Weitere Unis
          </motion.p>
          <motion.h2
            variants={fadeUp}
            id="related-heading"
            className="font-display font-bold text-3xl sm:text-4xl tracking-tight leading-[1.1] text-ink"
          >
            Diese Unis könnten dich auch interessieren
          </motion.h2>
        </motion.div>

        <motion.div
          variants={stagger(0.08)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {universities.map((uni) => (
            <motion.div key={uni.id} variants={fadeUp}>
              <Link
                href={uniDetailPath(uni.id)}
                className="card-flat group flex flex-col h-full bg-white/60 p-4 transition-all duration-300 hover:-translate-y-1"
              >
                <span className="relative block rounded-xl overflow-hidden aspect-[16/9] mb-4">
                  <Image
                    src={uniImagePath(uni.id)}
                    alt={uni.name}
                    width={750}
                    height={874}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 inline-flex items-center rounded-full bg-ink/80 text-paper backdrop-blur-sm px-3 py-1 text-xs font-bold uppercase tracking-wider">
                    {uni.state}
                  </span>
                </span>
                <span className="flex flex-col gap-2 px-1.5 pb-1.5 flex-1">
                  <span className="font-display font-bold text-lg leading-snug text-ink group-hover:text-accent transition-colors">
                    {uni.name}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-ink-soft">
                    <MapPin size={14} className="text-accent" aria-hidden="true" />
                    {uni.city} · {uni.type}
                  </span>
                  <span className="mt-auto inline-flex items-center gap-2 pt-2 text-sm font-semibold text-accent">
                    Mehr Infos
                    <HoverArrow size={14} />
                  </span>
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
